"use client";

import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import SmoothImage from "@/components/SmoothImage";
import { renderTextWithBreaks } from "@/utils/stripHTML";

interface HeroSectionProps {
  imageUrl?: string;
  slideImages?: string[];
  slideorvideo?: string;
  videourl?: string;
  title?: string;
  description?: string;
  subTitle?: string;
  buttonText?: string;
  watermark1?: string;
  watermark2?: string;
}

function formatVideoUrl(url: string = ""): {
  formattedUrl: string;
  isDirectVideo: boolean;
} {
  if (!url) return { formattedUrl: "", isDirectVideo: false };
  let cleanUrl = url.trim();

  const isDirect =
    cleanUrl.endsWith(".mp4") ||
    cleanUrl.endsWith(".webm") ||
    cleanUrl.endsWith(".ogg");

  if (isDirect) {
    return { formattedUrl: cleanUrl, isDirectVideo: true };
  }

  let videoId = "";
  if (cleanUrl.includes("youtube.com/watch?v=")) {
    videoId = cleanUrl.split("v=")[1]?.split("&")[0] || "";
  } else if (cleanUrl.includes("youtu.be/")) {
    videoId = cleanUrl.split("youtu.be/")[1]?.split("?")[0] || "";
  } else if (cleanUrl.includes("youtube.com/embed/")) {
    videoId = cleanUrl.split("youtube.com/embed/")[1]?.split("?")[0] || "";
  }

  if (videoId) {
    return {
      formattedUrl: `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&iv_load_policy=3&enablejsapi=1&playsinline=1`,
      isDirectVideo: false,
    };
  }

  return { formattedUrl: cleanUrl, isDirectVideo: false };
}

const LIGHT_SPECKS = [
  {
    top: "12%",
    left: "6%",
    width: "5px",
    height: "5px",
    radius: "rounded-full",
    delay: "0s",
    duration: "6s",
  },
  {
    top: "18%",
    left: "18%",
    width: "12px",
    height: "7px",
    radius: "rounded-[40%_60%_70%_30%]",
    delay: "1.8s",
    duration: "7.5s",
  },
  {
    top: "14%",
    left: "34%",
    width: "5px",
    height: "8px",
    radius: "rounded-[60%_40%_50%_50%]",
    delay: "3.2s",
    duration: "8s",
  },
  {
    top: "25%",
    left: "10%",
    width: "8px",
    height: "8px",
    radius: "rounded-full",
    delay: "0.4s",
    duration: "5.5s",
  },
  {
    top: "28%",
    left: "28%",
    width: "10px",
    height: "6px",
    radius: "rounded-[50%_50%_30%_70%]",
    delay: "2.1s",
    duration: "6.8s",
  },

  {
    top: "36%",
    left: "8%",
    width: "7px",
    height: "7px",
    radius: "rounded-full",
    delay: "0.9s",
    duration: "6.5s",
  },
  {
    top: "40%",
    left: "22%",
    width: "13px",
    height: "7px",
    radius: "rounded-[30%_70%_60%_40%]",
    delay: "2.4s",
    duration: "9s",
  },
  {
    top: "45%",
    left: "38%",
    width: "6px",
    height: "6px",
    radius: "rounded-full",
    delay: "1.3s",
    duration: "7s",
  },

  {
    top: "54%",
    left: "5%",
    width: "9px",
    height: "9px",
    radius: "rounded-full",
    delay: "1.6s",
    duration: "6.2s",
  },
  {
    top: "60%",
    left: "16%",
    width: "6px",
    height: "6px",
    radius: "rounded-full",
    delay: "4.1s",
    duration: "7.2s",
  },
  {
    top: "52%",
    left: "30%",
    width: "14px",
    height: "8px",
    radius: "rounded-[60%_40%_70%_30%]",
    delay: "0.7s",
    duration: "7.8s",
  },

  {
    top: "70%",
    left: "12%",
    width: "10px",
    height: "6px",
    radius: "rounded-[50%_50%_30%_70%]",
    delay: "1.2s",
    duration: "8.5s",
  },
  {
    top: "78%",
    left: "25%",
    width: "6px",
    height: "6px",
    radius: "rounded-full",
    delay: "3.6s",
    duration: "7.4s",
  },
  {
    top: "84%",
    left: "14%",
    width: "8px",
    height: "8px",
    radius: "rounded-full",
    delay: "4.5s",
    duration: "6.3s",
  },
];

export default function HeroSection({
  imageUrl,
  slideImages,
  slideorvideo = "slide",
  videourl,
  title = "Sống trọn giữa\ntâm điểm kết nối",
  description = "Nơi công việc, gia đình, thiên nhiên và tương lai cùng hội tụ, mở ra một cuộc sống trọn vẹn hơn mỗi ngày.",
  subTitle = "MỸ HẠNH · TÂY NINH · GIÁP TP.HCM",
  buttonText = "KHÁM PHÁ DRAGON HOME",
  watermark1 = "Live Connected",
  watermark2 = "Live Well",
}: HeroSectionProps) {
  const imagesToSlide =
    slideImages && slideImages.length > 0
      ? slideImages
      : [imageUrl || "/images/hero_skyline.png"];

  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(0.5);
  const videoRef = useRef<HTMLVideoElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const userPausedRef = useRef(false);
  const userMutedRef = useRef(false);
  const hasAutoUnmutedRef = useRef(false);

  useEffect(() => {
    if (slideorvideo === "slide" && imagesToSlide.length > 1) {
      const timer = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % imagesToSlide.length);
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [slideorvideo, imagesToSlide.length]);

  const { formattedUrl: formattedVideoUrl, isDirectVideo } = formatVideoUrl(
    videourl || "",
  );

  const sendYouTubeCommand = (func: string, args: any[] = []) => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(
        JSON.stringify({
          event: "command",
          func: func,
          args: args,
        }),
        "*",
      );
    }
  };

  const pauseVideo = () => {
    setIsPlaying(false);
    if (isDirectVideo) {
      if (videoRef.current) {
        videoRef.current.pause();
      }
    } else {
      sendYouTubeCommand("pauseVideo", []);
    }
  };

  const playVideo = () => {
    setIsPlaying(true);
    if (isDirectVideo) {
      if (videoRef.current) {
        videoRef.current.play().catch(() => {});
      }
    } else {
      sendYouTubeCommand("playVideo", []);
    }
  };

  const togglePlay = () => {
    if (isPlaying) {
      userPausedRef.current = true;
      pauseVideo();
    } else {
      userPausedRef.current = false;
      playVideo();
    }
  };

  const handleVolumeChange = (newVolume: number) => {
    setVolume(newVolume);
    const shouldMute = newVolume === 0;
    setIsMuted(shouldMute);
    userMutedRef.current = shouldMute;

    if (isDirectVideo) {
      if (videoRef.current) {
        videoRef.current.volume = newVolume;
        videoRef.current.muted = shouldMute;
      }
    } else {
      sendYouTubeCommand("setVolume", [Math.round(newVolume * 100)]);
      if (shouldMute) {
        sendYouTubeCommand("mute", []);
      } else {
        sendYouTubeCommand("unMute", []);
      }
    }
  };

  const toggleMute = () => {
    const nextMutedState = !isMuted;
    setIsMuted(nextMutedState);
    userMutedRef.current = nextMutedState;

    if (nextMutedState) {
      if (isDirectVideo) {
        if (videoRef.current) videoRef.current.muted = true;
      } else {
        sendYouTubeCommand("mute", []);
      }
    } else {
      const activeVolume = volume === 0 ? 0.5 : volume;
      if (volume === 0) setVolume(0.5);

      if (isDirectVideo) {
        if (videoRef.current) {
          videoRef.current.muted = false;
          videoRef.current.volume = activeVolume;
        }
      } else {
        sendYouTubeCommand("unMute", []);
        sendYouTubeCommand("setVolume", [Math.round(activeVolume * 100)]);
      }
    }
  };

  // Pause video when section scrolls out of view, auto unmute at 50% volume on first scroll into view (unless user muted)
  useEffect(() => {
    if (slideorvideo !== "video" || !formattedVideoUrl) return;

    const applyAutoUnmute = () => {
      if (userMutedRef.current) return;

      const targetVol = volume > 0 ? volume : 0.5;
      setVolume(targetVol);
      setIsMuted(false);

      if (isDirectVideo) {
        if (videoRef.current) {
          videoRef.current.volume = targetVol;
          videoRef.current.muted = false;
        }
      } else {
        sendYouTubeCommand("unMute", []);
        sendYouTubeCommand("setVolume", [Math.round(targetVol * 100)]);
      }
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          pauseVideo();
        } else {
          if (!userPausedRef.current) {
            playVideo();
          }
          if (!hasAutoUnmutedRef.current) {
            hasAutoUnmutedRef.current = true;
            applyAutoUnmute();
          } else if (!userMutedRef.current) {
            applyAutoUnmute();
          }
        }
      },
      { threshold: 0.15 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    const handleFirstGesture = () => {
      if (!userMutedRef.current) {
        applyAutoUnmute();
      }
    };

    window.addEventListener("pointerdown", handleFirstGesture, { once: true });
    window.addEventListener("keydown", handleFirstGesture, { once: true });

    return () => {
      observer.disconnect();
      window.removeEventListener("pointerdown", handleFirstGesture);
      window.removeEventListener("keydown", handleFirstGesture);
    };
  }, [slideorvideo, isDirectVideo, formattedVideoUrl, volume]);

  const handleScrollDown = () => {
    const nextSection =
      document.getElementById("tin-tuc") ||
      document.getElementById("gioi-thieu") ||
      document.getElementById("dang-ky-bao-gia");
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      id="banner"
      ref={sectionRef}
      className="relative w-full h-auto lg:h-screen lg:min-h-screen bg-[#053328] text-white overflow-x-hidden flex flex-col justify-start lg:justify-between"
    >
      <style>{`
        .font-lora {
          font-family: var(--font-lora), Georgia, serif;
        }
        @keyframes sunlightTwinkle {
          0%, 100% {
            opacity: 0.05;
            transform: scale(0.85) translate(0, 0);
          }
          50% {
            opacity: 0.35;
            transform: scale(1.15) translate(-2px, 2px);
          }
        }
      `}</style>

      {/* DESKTOP ONLY TREE SHADOW & SUNLIGHT SPECKS BACKGROUND */}
      <div className="hidden lg:block absolute top-0 left-0 w-[55%] h-full pointer-events-none z-[16] overflow-hidden">
        {/* CLEAR & SHARP TREE SHADOW OVERLAY */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-100">
          <Image
            src="/images/hero_deco_shadow.png"
            alt="Hero Tree Shadow Overlay"
            fill
            sizes="(max-width: 1024px) 100vw, 55vw"
            priority
            className="object-cover object-left-top contrast-150 brightness-110"
          />
        </div>

        {/* DAPPLED SUNLIGHT SPECKS */}
        <div className="absolute top-0 left-0 w-full lg:w-[88%] h-full pointer-events-none">
          {LIGHT_SPECKS.map((speck, idx) => (
            <div
              key={idx}
              className={`absolute ${speck.radius} bg-[#FFF8E7] blur-[2px] shadow-[0_0_8px_rgba(255,248,231,0.5)]`}
              style={{
                top: speck.top,
                left: speck.left,
                width: speck.width,
                height: speck.height,
                animation: `sunlightTwinkle ${speck.duration} infinite ease-in-out ${speck.delay}`,
              }}
            />
          ))}
        </div>
      </div>

      {/* MOBILE/TABLET TOP HEADER (SEPARATE FULLY CLEAR TOP TREE SHADOW) */}
      <div className="lg:hidden relative z-20 w-full pt-20 sm:pt-24 pb-8 px-[5%] md:px-[6%] bg-[#053328] pointer-events-auto overflow-hidden">
        {/* MOBILE TOP TREE SHADOW IMAGE - 100% CLEAR */}
        <div className="absolute inset-0 pointer-events-none opacity-100 -z-0">
          <Image
            src="/images/hero_deco_shadow.png"
            alt="Mobile Top Tree Shadow"
            fill
            sizes="(max-width: 1024px) 100vw, 50vw"
            priority
            className="object-cover object-top contrast-150 brightness-110"
          />
        </div>

        <div className="relative z-10 flex flex-col space-y-3 max-w-xl mx-auto">
          <div className="flex items-center gap-3">
            <span className="w-8 h-[1px] bg-[#C9AB81]" />
            <span className="text-[#C9AB81] text-xs tracking-[0.2em] font-semibold uppercase">
              {subTitle}
            </span>
          </div>

          <h1
            className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-lora font-normal text-white tracking-tight drop-shadow-md title-leading"
            style={{ lineHeight: 1.15 }}
          >
            {renderTextWithBreaks(title)}
          </h1>
        </div>
      </div>

      {/* BACKGROUND MEDIA CONTAINER (RESPONSIVE SCALE FOR VIDEO & IMAGE ON MOBILE, ABSOLUTE RIGHT ON DESKTOP) */}
      <div
        className={`relative w-full overflow-hidden z-10 flex-shrink-0 lg:aspect-none lg:h-full lg:w-[68%] lg:absolute lg:top-0 lg:right-0 ${
          slideorvideo === "video"
            ? "aspect-video sm:aspect-[16/9.5] sm:h-full"
            : "aspect-[16/10] sm:aspect-[16/9.5]"
        }`}
      >
        {slideorvideo === "video" && formattedVideoUrl ? (
          isDirectVideo ? (
            <video
              ref={videoRef}
              src={formattedVideoUrl}
              autoPlay
              loop
              muted={isMuted}
              playsInline
              className="w-full h-full object-cover object-center lg:object-right"
            />
          ) : (
            <iframe
              ref={iframeRef}
              src={formattedVideoUrl}
              title="Hero Background Video"
              allow="autoplay; encrypted-media"
              className="w-full h-full border-none pointer-events-none scale-105 sm:scale-105 lg:scale-[1.7] object-cover origin-center"
            />
          )
        ) : (
          <div className="relative w-full h-full overflow-hidden">
            {imagesToSlide.map((src, idx) => (
              <div
                key={src + idx}
                className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                  idx === currentSlide ? "opacity-100 z-10" : "opacity-0 z-0"
                }`}
              >
                <SmoothImage
                  src={src}
                  alt={`Dragon Home Building ${idx + 1}`}
                  fill
                  sizes=" 100vw"
                  priority={idx === 0}
                  className="object-cover object-center lg:object-right contrast-[1.05] brightness-[1.05]"
                />
              </div>
            ))}
            {imagesToSlide.length > 1 && (
              <div className="absolute bottom-10 right-4 sm:bottom-12 sm:right-6 lg:bottom-6 lg:right-6 z-20 flex gap-2 pointer-events-auto">
                {imagesToSlide.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentSlide(idx)}
                    className={`h-2.5 rounded-full transition-all cursor-pointer ${
                      idx === currentSlide
                        ? "bg-[#E5CFB0] w-6"
                        : "bg-white/40 hover:bg-white/70 w-2.5"
                    }`}
                    aria-label={`Go to slide ${idx + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* DESKTOP GRADIENT OVERLAY */}
      <div className="hidden lg:block absolute inset-0 z-15 pointer-events-none">
        {/* PERFECT BALANCED EMERALD TONE GRADIENT (#053328) */}
        <div
          className="absolute inset-0 z-10 pointer-events-none"
          style={{
            background:
              "linear-gradient(to right, #053328 0%, #053328 32%, rgba(5, 51, 40, 0.84) 48%, rgba(5, 51, 40, 0.3) 72%, transparent 95%)",
          }}
        />

        {/* SOFT TOP & BOTTOM GRADIENTS */}
        <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-[#053328]/60 via-[#053328]/20 to-transparent z-10 pointer-events-none" />
        <div className="absolute bottom-0 left-0 right-0 h-28 bg-gradient-to-t from-[#053328]/75 via-[#053328]/20 to-transparent z-10 pointer-events-none" />
      </div>

      {/* MAIN CONTENT CONTAINER */}
      <div className="relative z-20 max-w-7xl mx-auto w-[90%] md:w-[88%] grid grid-cols-1 lg:grid-cols-12 gap-8 items-center h-full pt-6 lg:pt-20 pb-12 mt-0">
        {/* MOBILE/TABLET BOTTOM SEPARATE TREE SHADOW BACKDROP - 100% FULL WIDTH */}
        <div className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-screen bg-[#053328] lg:hidden -z-10 pointer-events-none overflow-hidden">
          <Image
            src="/images/hero_deco_shadow.png"
            alt="Mobile Bottom Tree Shadow"
            fill
            sizes="(max-width: 1024px) 100vw, 50vw"
            className="object-cover object-bottom opacity-100 contrast-150 brightness-110"
          />
        </div>

        {/* LEFT COLUMN: TEXT CONTENT */}
        <div className="lg:col-span-6 flex flex-col justify-center space-y-6 md:space-y-8 z-20">
          {/* DESKTOP ONLY SUBTITLE AND TITLE */}
          <div className="hidden lg:flex items-center gap-3">
            <span className="w-8 h-[1px] bg-[#C9AB81]" />
            <span className="text-[#C9AB81] text-xs tracking-[0.2em] font-semibold uppercase">
              {subTitle}
            </span>
          </div>

          <h1
            className="hidden lg:block text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-lora font-normal text-white tracking-tight drop-shadow-md title-leading"
            style={{ lineHeight: 1.15 }}
          >
            {renderTextWithBreaks(title)}
          </h1>

          <p className="text-white/80 text-sm sm:text-base font-light leading-relaxed max-w-lg drop-shadow pt-2 lg:pt-0">
            {renderTextWithBreaks(description)}
          </p>

          <div className="pt-2 flex flex-wrap items-center gap-5">
            <button
              onClick={handleScrollDown}
              className="group inline-flex items-center gap-4 px-6 py-3.5 rounded-full border border-white/30 hover:border-[#E5CFB0] bg-[#042A21]/40 hover:bg-white/10 transition-all cursor-pointer backdrop-blur-md"
            >
              <span className="text-xs uppercase tracking-[0.18em] font-semibold text-white group-hover:text-[#E5CFB0] transition-colors">
                {buttonText}
              </span>
              <span className="w-9 h-9 rounded-full bg-[#E5CFB0] text-[#06523F] flex items-center justify-center font-bold text-sm shadow group-hover:scale-105 transition-transform">
                ↓
              </span>
            </button>
          </div>
        </div>

        {/* RIGHT COLUMN - WATERMARK TEXT AT BOTTOM WITH VIDEO CONTROLS BELOW */}
        <div className="lg:col-span-6 relative h-full flex flex-col justify-end items-start lg:items-end pointer-events-none pb-4 md:pb-10 pt-4 lg:pt-0">
          <div className="text-left lg:text-right pointer-events-auto z-20 flex flex-col items-start lg:items-end gap-3">
            <div>
              <p className="font-lora italic text-2xl sm:text-3xl md:text-4xl text-white/90 leading-tight drop-shadow-lg">
                {renderTextWithBreaks(watermark1)}
              </p>
              <p className="font-lora italic font-bold text-2xl sm:text-3xl md:text-4xl text-[#E5CFB0] leading-tight drop-shadow-lg">
                {renderTextWithBreaks(watermark2)}
              </p>
            </div>

            {/* VIDEO CONTROLS BELOW WATERMARK TEXT */}
            {slideorvideo === "video" && formattedVideoUrl && (
              <div className="flex items-center gap-3 pt-2">
                {/* PLAY / PAUSE BUTTON */}
                <button
                  type="button"
                  onClick={togglePlay}
                  className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-[#053328]/80 hover:bg-[#053328] border border-white/20 hover:border-[#E5CFB0] text-white flex items-center justify-center backdrop-blur-md shadow-lg transition-all transform hover:scale-105 active:scale-95 cursor-pointer group"
                  aria-label={isPlaying ? "Dừng video" : "Phát video"}
                  title={isPlaying ? "Dừng video" : "Phát video"}
                >
                  {isPlaying ? (
                    <svg
                      className="w-5 h-5 fill-current text-white group-hover:text-[#E5CFB0] transition-colors"
                      viewBox="0 0 24 24"
                    >
                      <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                    </svg>
                  ) : (
                    <svg
                      className="w-5 h-5 fill-current text-white group-hover:text-[#E5CFB0] transition-colors ml-0.5"
                      viewBox="0 0 24 24"
                    >
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  )}
                </button>

                {/* VOLUME CONTROL BAR (ALWAYS VISIBLE SLIDER) */}
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#053328]/80 hover:bg-[#053328] border border-white/20 hover:border-[#E5CFB0] backdrop-blur-md shadow-lg transition-all">
                  <button
                    type="button"
                    onClick={toggleMute}
                    className="w-7 h-7 text-white flex items-center justify-center transition-all transform hover:scale-110 active:scale-95 cursor-pointer group"
                    aria-label={isMuted || volume === 0 ? "Bật tiếng" : "Tắt tiếng"}
                    title={isMuted || volume === 0 ? "Bật tiếng" : "Tắt tiếng"}
                  >
                    {isMuted || volume === 0 ? (
                      <svg
                        className="w-5 h-5 fill-current text-white group-hover:text-[#E5CFB0] transition-colors"
                        viewBox="0 0 24 24"
                      >
                        <path d="M4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73 4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
                      </svg>
                    ) : volume < 0.5 ? (
                      <svg
                        className="w-5 h-5 fill-current text-white group-hover:text-[#E5CFB0] transition-colors"
                        viewBox="0 0 24 24"
                      >
                        <path d="M18.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM5 9v6h4l5 5V4L9 9H5z" />
                      </svg>
                    ) : (
                      <svg
                        className="w-5 h-5 fill-current text-white group-hover:text-[#E5CFB0] transition-colors"
                        viewBox="0 0 24 24"
                      >
                        <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
                      </svg>
                    )}
                  </button>

                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={isMuted ? 0 : volume}
                    onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                    className="w-20 sm:w-24 h-1.5 bg-white/30 rounded-lg appearance-none cursor-pointer accent-[#E5CFB0] hover:bg-white/40 transition-colors"
                    aria-label="Thanh điều chỉnh âm lượng"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
